import { createRootRoute, Outlet, useLocation } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { DevErrorBoundary } from '@/components'
import { AppProvider } from '@/context'
import { initializeNewRelic } from '@/utils/newrelic'
import { DashboardLayout } from '@/components/layout/dashboard-layout'
import { Security } from '@okta/okta-react'
import { oktaAuth } from '../config/okta'

// Create a client with enhanced configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (except 408, 429)
        if (error instanceof Error && 'status' in error) {
          const status = (error as any).status
          if (status >= 400 && status < 500 && status !== 408 && status !== 429) {
            return false
          }
        }
        return failureCount < 3
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
})

// Initialize New Relic
initializeNewRelic()

export const Route = createRootRoute({
  component: RootComponent,
})

function RootComponent() {
  const location = useLocation()
  const restoreOriginalUri = (_oktaAuth: any, originalUri: string) => {
    window.location.replace(originalUri || '/')
  }

  // Check if current route is a login/auth route that should not have layout
  const isAuthRoute = location.pathname.startsWith('/login')

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <QueryClientProvider client={queryClient}>
        <DevErrorBoundary>
          <AppProvider>
            {isAuthRoute ? (
              // Render login pages without dashboard layout
              <Outlet />
            ) : (
              // Render dashboard pages with full layout
              <DashboardLayout />
            )}
          </AppProvider>
        </DevErrorBoundary>
      </QueryClientProvider>
    </Security>
  )
}
